import {
    Box,
    Divider,
    FormHelperText,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import FormCheckbox from '../../../shared/FormCheckbox';
import FormCounter from '../../../shared/FormCounter';
import FormTextField from '../../../shared/FormTextField';

const BedroomForm = ({ control }: { control: any }) => {
    const sleepingItems: Array<{ label: string; name: string }> = [
        { label: 'Single bed', name: 'singleBed' },
        { label: 'Double bed', name: 'doubleBed' },
        { label: 'Queen bed', name: 'queenBed' },
        { label: 'King bed', name: 'kingBed' },
        { label: 'Bunk bed', name: 'bunkBed' },
        { label: 'Sofa bed', name: 'sofaBed' },
        { label: 'Couch', name: 'couch' },
        { label: 'Baby cot', name: 'babyCot' },
    ];

    const amenityItems: Array<{ label: string; name: string }> = [
        { label: 'Blackout curtains', name: 'blackOutCurtains' },
        { label: 'Wardrobe', name: 'wardrobe' },
        { label: 'Extra pillows & blankets', name: 'extraPillowsAndBlankets' },
        { label: 'Iron', name: 'iron' },
        { label: 'Safe', name: 'safe' },
        { label: 'TV', name: 'tv' },
        { label: 'Ensuite', name: 'ensuite' },
        { label: 'Monitor', name: 'monitor' },
    ];

    return (
        <>
            <Stack spacing={2}>
                <Box sx={{ px: 0.5 }}>
                    <Stack spacing={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                            }}
                        >
                            <Typography
                                component='h2'
                                variant='h3'
                                color='text.primary'
                                sx={{ mb: 0 }}
                            >
                                Basics
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                Give the room a friendly name guests will
                                recognize.
                            </Typography>
                        </Box>
                        <Divider />
                        <Box>
                            <InputLabel required>Name</InputLabel>
                            <FormTextField
                                name='assignedName'
                                control={control}
                                placeholder='i.e. Sunrise Room'
                            />
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ px: 0.5 }}>
                    <Stack spacing={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                            }}
                        >
                            <Typography
                                component='h2'
                                variant='h3'
                                color='text.primary'
                                sx={{ mb: 0 }}
                            >
                                Sleeping arrangements
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                Add what’s in the room (beds, sofa bed, cot).
                            </Typography>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr',
                                },
                                columnGap: 4,
                                rowGap: 1.25,
                                width: '100%',
                            }}
                        >
                            {sleepingItems.map((item) => (
                                <Box
                                    key={item.name}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 2,
                                        minWidth: 0,
                                    }}
                                >
                                    <InputLabel
                                        htmlFor={`${item.name}__input`}
                                        sx={{ m: 0 }}
                                    >
                                        {item.label}
                                    </InputLabel>
                                    <Box sx={{ flexShrink: 0 }}>
                                        <FormCounter
                                            id={`${item.name}__input`}
                                            name={item.name}
                                            disabled={false}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ px: 0.5 }}>
                    <Stack spacing={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                            }}
                        >
                            <Typography
                                component='h2'
                                variant='h3'
                                color='text.primary'
                                sx={{ mb: 0 }}
                            >
                                Amenities
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                Select what guests can expect in this room.
                            </Typography>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr',
                                },
                                columnGap: 4,
                                rowGap: 1.25,
                                width: '100%',
                            }}
                        >
                            {amenityItems.map((item) => (
                                <Box
                                    key={item.name}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <FormCheckbox
                                        id={`${item.name}__checkbox`}
                                        name={item.name}
                                        control={control}
                                        sx={{ p: 0 }}
                                    />
                                    <InputLabel
                                        htmlFor={`${item.name}__checkbox`}
                                        sx={{ m: 0 }}
                                    >
                                        {item.label}
                                    </InputLabel>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ px: 0.5 }}>
                    <Stack spacing={1.5}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                            }}
                        >
                            <Typography
                                component='h2'
                                variant='h3'
                                color='text.primary'
                                sx={{ mb: 0 }}
                            >
                                Capacity
                            </Typography>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                Set the max guests and any workspace setup.
                            </Typography>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: '1fr 1fr',
                                },
                                columnGap: 4,
                                rowGap: 1.25,
                                width: '100%',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    minWidth: 0,
                                }}
                            >
                                <InputLabel htmlFor='desk__input' sx={{ m: 0 }}>
                                    Desk
                                </InputLabel>
                                <Box sx={{ flexShrink: 0 }}>
                                    <FormCounter
                                        id='desk__input'
                                        name='desk'
                                        disabled={false}
                                    />
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    minWidth: 0,
                                }}
                            >
                                <InputLabel
                                    htmlFor='sleepingCapacity__input'
                                    sx={{ m: 0 }}
                                >
                                    Sleeping capacity
                                </InputLabel>
                                <Box sx={{ flexShrink: 0 }}>
                                    <FormCounter
                                        id='sleepingCapacity__input'
                                        name='sleepingCapacity'
                                        disabled={false}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </>
    );
};

export default BedroomForm;
